import React from 'react'
import PropTypes from 'prop-types'
import { Radio } from 'antd'
import { classNames } from 'utils/utils'
import ChecPopConfirm from 'components/patterns/ChecPopConfirm'
import { isObject } from 'utils/types'
import { compose, isEmpty } from 'ramda'

import InputLabel from 'components/patterns/Inputs/InputLabel'

import withStyle from './RadioButtonGroup.style'

import 'styles/AntOverrides.css'
import 'styles/ChecComponents.css'

const RadioButtonGroup = ({
  currentValue,
  buttonStyle,
  size,
  onChange,
  disabled,
  buttons,
  style,
  className,
  label,
  vertical,
  name,
  hint,
  tooltip,
  required,
  icon,
  popConfirm,
  radioGroupClassName,
}) => {
  const btns = buttons.map(
    ({ value, text = '', buttonClass, disabled: disabledOption, image }) => {
      const btn = (
        <Radio.Button
          key={`${value}-${text}`}
          value={value}
          className={classNames('chec-radio-button', buttonClass)}
          disabled={disabledOption}
          data-test-id={`${text.trim()}-radio-btn`}
        >
          {text || image || value}
        </Radio.Button>
      )
      return isObject(popConfirm) ? (
        <ChecPopConfirm
          showConfirm={popConfirm.showConfirm}
          title={popConfirm.title}
          okText={popConfirm.okText}
          okType={popConfirm.okType}
          cancelType={popConfirm.cancelType}
          cancelText={popConfirm.cancelText}
          onConfirm={popConfirm.onConfirm}
          placement={popConfirm.placement}
          icon={popConfirm.icon}
          key={`${value}-${text}`}
        >
          {btn}
        </ChecPopConfirm>
      ) : (
        btn
      )
    }
  )

  return (
    <div className={classNames('form-field', className)}>
      {label && (
        <InputLabel
          label={label}
          hint={hint}
          tooltip={tooltip}
          htmlFor={name}
          size={size}
          vertical={vertical}
          required={required}
          icon={icon}
        />
      )}
      <Radio.Group
        className={classNames('chec-radio-group', radioGroupClassName)}
        style={style}
        value={currentValue}
        buttonStyle={buttonStyle}
        size={size}
        onChange={onChange}
        disabled={disabled}
        name={name}
      >
        {!isEmpty(buttons) && btns}
      </Radio.Group>
    </div>
  )
}

RadioButtonGroup.defaultProps = {
  size: 'default',
  buttonStyle: 'solid',
  disabled: false,
  vertical: false,
  name: 'buttonGroup',
  currentValue: null,
  popConfirm: null,
}

RadioButtonGroup.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.string,
  currentValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.number,
  ]),
  buttonStyle: PropTypes.oneOf(['outline', 'solid']),
  size: PropTypes.oneOf(['large', 'default', 'small']),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.number,
      ]).isRequired,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      disabled: PropTypes.bool,
    })
  ).isRequired,
  style: PropTypes.object,
  label: PropTypes.string,
  vertical: PropTypes.bool,
  name: PropTypes.string,
  hint: PropTypes.string,
  tooltip: PropTypes.string,
  popConfirm: PropTypes.shape({
    showConfirm: PropTypes.bool,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    children: PropTypes.node,
    onConfirm: PropTypes.func.isRequired,
    okText: PropTypes.string,
    okType: PropTypes.string,
    placement: PropTypes.string,
    className: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.object,
      PropTypes.string,
    ]),
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    cancelType: PropTypes.string,
    cancelText: PropTypes.string,
  }),
  radioGroupClassName: PropTypes.string,
}

export default compose(withStyle)(RadioButtonGroup)
